export const DEFAULT_THEME_PRESET = "navy-gold";
export const DEFAULT_PRIMARY_COLOR = "#000060";
export const DEFAULT_SECONDARY_COLOR = "#FFBB00";

const LEGACY_DEFAULT_SECONDARY_COLOR = "#FFD700";
const WHITE = "#FFFFFF";
const BLACK = "#000000";

const SHADE_BLEND_STOPS = {
  50: { target: WHITE, amount: 0.91 },
  100: { target: WHITE, amount: 0.76 },
  200: { target: WHITE, amount: 0.6 },
  300: { target: WHITE, amount: 0.42 },
  400: { target: WHITE, amount: 0.16 },
  500: { target: null, amount: 0 },
  600: { target: BLACK, amount: 0.12 },
  700: { target: BLACK, amount: 0.24 },
  800: { target: BLACK, amount: 0.38 },
  900: { target: BLACK, amount: 0.52 },
};

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

function normalizeHexColor(value) {
  if (typeof value !== "string") return null;

  const normalized = value.trim().replace(/^#/, "");
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return null;

  return `#${normalized.toUpperCase()}`;
}

function hexToRgbArray(color) {
  const normalized = normalizeHexColor(color);
  if (!normalized) return null;

  return [1, 3, 5].map((index) => parseInt(normalized.slice(index, index + 2), 16));
}

function rgbArrayToHex([r, g, b]) {
  return `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function hexToRgbString(color) {
  const rgb = hexToRgbArray(color);
  if (!rgb) return null;

  return rgb.join(" ");
}

function mixHexColors(baseColor, mixColor, amount) {
  const base = hexToRgbArray(baseColor);
  const mix = hexToRgbArray(mixColor);
  if (!base || !mix) return null;

  const ratio = Math.max(0, Math.min(1, amount));
  const blended = base.map((channel, index) => {
    return channel + (mix[index] - channel) * ratio;
  });

  return rgbArrayToHex(blended);
}

function buildThemePalette(baseColor) {
  const normalizedBase = normalizeHexColor(baseColor);
  if (!normalizedBase) {
    return null;
  }

  return Object.entries(SHADE_BLEND_STOPS).reduce((palette, [shade, blend]) => {
    palette[shade] = blend.target
      ? mixHexColors(normalizedBase, blend.target, blend.amount)
      : normalizedBase;
    return palette;
  }, {});
}

export function normalizeThemeColors({
  primary = DEFAULT_PRIMARY_COLOR,
  secondary = DEFAULT_SECONDARY_COLOR,
  preset,
} = {}) {
  const normalizedPrimary = normalizeHexColor(primary) ?? DEFAULT_PRIMARY_COLOR;
  let normalizedSecondary = normalizeHexColor(secondary) ?? DEFAULT_SECONDARY_COLOR;
  const normalizedPreset = preset ?? "custom";
  const isLegacyDefaultPair =
    normalizedPrimary === DEFAULT_PRIMARY_COLOR &&
    normalizedSecondary === LEGACY_DEFAULT_SECONDARY_COLOR;

  if (isLegacyDefaultPair || (
    normalizedPreset === DEFAULT_THEME_PRESET &&
    normalizedSecondary === LEGACY_DEFAULT_SECONDARY_COLOR
  )) {
    normalizedSecondary = DEFAULT_SECONDARY_COLOR;
  }

  return {
    primary: normalizedPrimary,
    secondary: normalizedSecondary,
    preset: normalizedPreset,
  };
}

export function getThemeCssVariables({ primary, secondary, preset } = {}) {
  const normalizedTheme = normalizeThemeColors({ primary, secondary, preset });
  const primaryPalette = buildThemePalette(normalizedTheme.primary);
  const secondaryPalette = buildThemePalette(normalizedTheme.secondary);

  return {
    "--tw-primary": hexToRgbString(normalizedTheme.primary),
    "--tw-primary-50": hexToRgbString(primaryPalette[50]),
    "--tw-primary-100": hexToRgbString(primaryPalette[100]),
    "--tw-primary-200": hexToRgbString(primaryPalette[200]),
    "--tw-primary-300": hexToRgbString(primaryPalette[300]),
    "--tw-primary-400": hexToRgbString(primaryPalette[400]),
    "--tw-primary-500": hexToRgbString(primaryPalette[500]),
    "--tw-primary-600": hexToRgbString(primaryPalette[600]),
    "--tw-primary-700": hexToRgbString(primaryPalette[700]),
    "--tw-primary-800": hexToRgbString(primaryPalette[800]),
    "--tw-primary-900": hexToRgbString(primaryPalette[900]),
    "--tw-secondary": hexToRgbString(normalizedTheme.secondary),
    "--tw-secondary-50": hexToRgbString(secondaryPalette[50]),
    "--tw-secondary-100": hexToRgbString(secondaryPalette[100]),
    "--tw-secondary-200": hexToRgbString(secondaryPalette[200]),
    "--tw-secondary-300": hexToRgbString(secondaryPalette[300]),
    "--tw-secondary-400": hexToRgbString(secondaryPalette[400]),
    "--tw-secondary-500": hexToRgbString(secondaryPalette[500]),
    "--tw-secondary-600": hexToRgbString(secondaryPalette[600]),
    "--tw-secondary-700": hexToRgbString(secondaryPalette[700]),
    "--tw-secondary-800": hexToRgbString(secondaryPalette[800]),
    "--tw-secondary-900": hexToRgbString(secondaryPalette[900]),
    "--color-primary": normalizedTheme.primary,
    "--color-primary-hover": primaryPalette[600],
    "--color-primary-light": primaryPalette[50],
    "--color-secondary": normalizedTheme.secondary,
    "--color-secondary-hover": secondaryPalette[600],
    "--color-secondary-light": secondaryPalette[50],
    "--color-border-focus": normalizedTheme.primary,
  };
}
